import React, { useEffect, useState } from "react";
import "./SharedNewsModal.css";
import { formatRelativeTime } from "../../utils/formatRelativeTime";
import { toPersianDigits } from "../../utils/toPersianDigits";
import RetryLogoImage from "../media/RetryLogoImage";
import { safeHttpUrl } from "../../utils/safeUrl";

const getNewsImageUrl = (summary = "") => {
  if (typeof summary !== "string") return null;

  const imgMatch = summary.match(/<img[^>]+src=["']([^"']+)["']/i);
  return imgMatch ? imgMatch[1] : null;
};

const getPlainSummary = (summary = "") => {
  if (typeof summary !== "string") return "";

  return (
    toPersianDigits(
      summary
        .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
        .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, "")
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/gi, " ")
        .replace(/&amp;/gi, "&")
        .replace(/&quot;/gi, '"')
        .replace(/&#39;/gi, "'")
        .replace(/\s+/g, " ")
        .trim()
    ) || ""
  );
};

const SharedNewsModal = ({
  news,
  favicon,
  isLoading = false,
  onClose,
  onOpenOriginal,
}) => {
  const imageUrl = getNewsImageUrl(news?.summary);
  const plainSummary = getPlainSummary(news?.summary);
  const relativeTime = news?.published_str
    ? formatRelativeTime(news.published_str)
    : "";

  const [failedImageUrl, setFailedImageUrl] = useState(null);
  const [loadedImageUrl, setLoadedImageUrl] = useState(null);

  const shouldShowImage = Boolean(imageUrl) && failedImageUrl !== imageUrl;
  const isImageLoaded = shouldShowImage && loadedImageUrl === imageUrl;

  useEffect(() => {
    setLoadedImageUrl(null);
    setFailedImageUrl(null);
  }, [imageUrl]);

  const handleImageFailure = () => {
    if (imageUrl) {
      setFailedImageUrl(imageUrl);
    }
  };

  const handleImageLoad = (event) => {
    const image = event.currentTarget;

    if (!image.complete || image.naturalWidth === 0) {
      handleImageFailure();
      return;
    }

    setLoadedImageUrl(imageUrl);
  };

  if (!news && !isLoading) return null;

  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget) {
      onClose?.();
    }
  };

  const handleOpenOriginal = () => {
    const safeLink = safeHttpUrl(news?.link);
    if (!safeLink) return;

    onOpenOriginal?.(news);

    if (typeof document === "undefined") return;

    const anchor = document.createElement("a");
    anchor.href = safeLink;
    anchor.target = "_blank";
    anchor.rel = "noopener noreferrer";
    anchor.click();
  };

  return (
    <div
      className="shared-news-modal"
      onMouseDown={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label="خبر اشتراک گذاری شده"
    >
      <article
        className="shared-news-modal__card"
        onMouseDown={(event) => event.stopPropagation()}
      >
        {isLoading ? (
          <div className="shared-news-modal__state">
            در حال بارگذاری خبر...
          </div>
        ) : (
          <>
            <div className="shared-news-modal__content">
              <div className="shared-news-modal__media">
                {shouldShowImage && (
                  <img
                    src={imageUrl}
                    alt=""
                    className={`shared-news-modal__image${
                      !isImageLoaded
                        ? " shared-news-modal__image--loading"
                        : ""
                    }`}
                    loading="lazy"
                    decoding="async"
                    onError={handleImageFailure}
                    onLoad={handleImageLoad}
                  />
                )}

                {!isImageLoaded && (
                  <div className="shared-news-modal__placeholder">
                    {favicon ? (
                      <RetryLogoImage
                        src={favicon}
                        alt=""
                        className="shared-news-modal__placeholder-logo"
                        fallbackText={news?.source?.charAt(0) || "N"}
                        decoding="async"
                      />
                    ) : (
                      <span>{news?.source?.charAt(0) || "N"}</span>
                    )}
                  </div>
                )}
              </div>

              <div className="shared-news-modal__body">
                <div className="shared-news-modal__meta">
                  {favicon && (
                    <span className="shared-news-modal__source-icon-wrapper">
                      <RetryLogoImage
                        src={favicon}
                        alt=""
                        className="shared-news-modal__source-icon"
                        fallbackText={news?.source?.charAt(0) || "N"}
                        loading="lazy"
                        decoding="async"
                      />
                    </span>
                  )}

                  <span className="shared-news-modal__source">
                    {toPersianDigits(news?.source)}
                  </span>

                  {relativeTime && (
                    <>
                      <span className="shared-news-modal__dot">•</span>
                      <time className="shared-news-modal__time">
                        {relativeTime}
                      </time>
                    </>
                  )}
                </div>

                <h1 className="shared-news-modal__title">
                  {toPersianDigits(news?.title)}
                </h1>

                {plainSummary && (
                  <p className="shared-news-modal__summary">
                    {plainSummary}
                  </p>
                )}
              </div>
            </div>

            <div className="shared-news-modal__actions">
              <button
                type="button"
                className="shared-news-modal__button shared-news-modal__button--primary"
                onClick={handleOpenOriginal}
                disabled={!news?.link}
              >
                مشاهده خبر اصلی
              </button>

              <button
                type="button"
                className="shared-news-modal__button"
                onClick={onClose}
              >
                دیگه چه‌خبره
              </button>
            </div>
          </>
        )}
      </article>
    </div>
  );
};

export default SharedNewsModal;
export { getNewsImageUrl, getPlainSummary };